import { Group, Table, Tooltip, UnstyledButton } from "@mantine/core";

import { IconEdit, IconNote, IconTrash, Icon as TablerIcon } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { FEATURE_UNDER_DEVELOPMENT_LABEL } from "@/constants/ui";
import { BILLING_TABLE_MIN_WIDTH } from "@/encounters/constants/billing-table";
import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";

import { isStagedBillerSectionRow } from "../utils/staged-biller-section-rows";

/** Column widths, following the design's proportions (Figma 3079:84643). */
const COLUMN_WIDTHS = {
  procedureCode: "19%",
  codeDescription: "22%",
  modifiers: "12%",
  diagnosisCodes: "15%",
  units: "15%",
  actions: "17%",
} as const;

/** Shown where a row has nothing for a column, so an empty cell reads as deliberate. */
const EMPTY_CELL = "--";

/** The size the design draws the row's action icons at (Figma 2708:48611). */
const ACTION_ICON_SIZE = 24;

interface BillerSectionTableProps {
  rows: EncounterChargeItemRow[];
  /**
   * Deletes a row. Left off when the tab has no way to save what a deletion stages, or when the
   * practitioner is not allowed to delete. Without it, the delete action is shown the same way the
   * actions that are not built yet are shown.
   */
  onDelete?: (row: EncounterChargeItemRow) => void;
  /** Opens the picker on a row's procedure code. Left off for the same reasons `onDelete` is. */
  onEdit?: (row: EncounterChargeItemRow) => void;
  /**
   * Opens the Notes drawer on a row's Practitioner and Biller comments. A staged row has no
   * captured `ChargeItem` to read comments from, so its Notes action falls back to the
   * `Feature under development` state even when this is passed.
   */
  onOpenComments?: (row: EncounterChargeItemRow) => void;
}

/**
 * One row action: an icon button labelled with what the action will do.
 *
 * An action given an `onClick` is a working button. An action without one is still being built, so
 * it shows the app's `Feature under development` tooltip and does nothing when clicked, which is how
 * the app offers every action that is not ready.
 */
function BillerSectionRowAction({
  label,
  actionTestId,
  icon: Icon,
  color,
  onClick,
}: {
  /** The screen reader label for the action. Tests use it to find the button. */
  label: string;
  /** Carries the row's charge id, so a test or a selector can name one row's action. */
  actionTestId: string;
  icon: TablerIcon;
  /** Only the destructive action is coloured; the others take the table's own text colour. */
  color?: string;
  onClick?: () => void;
}) {
  const button = (
    <UnstyledButton aria-label={label} data-testid={actionTestId} onClick={onClick ?? (() => {})}>
      <Icon aria-hidden color={color} size={ACTION_ICON_SIZE} />
    </UnstyledButton>
  );

  if (onClick) return button;

  return <Tooltip label={FEATURE_UNDER_DEVELOPMENT_LABEL}>{button}</Tooltip>;
}

function BillerSectionTableRow({
  row,
  onDelete,
  onEdit,
  onOpenComments,
}: {
  row: EncounterChargeItemRow;
  onDelete?: (row: EncounterChargeItemRow) => void;
  onEdit?: (row: EncounterChargeItemRow) => void;
  onOpenComments?: (row: EncounterChargeItemRow) => void;
}) {
  const { id, procedureCode, codeDescription, modifiers, diagnosisCodes, units } = row;
  const canOpenComments = onOpenComments && !isStagedBillerSectionRow(id);

  return (
    <Table.Tr>
      <Table.Td>
        <TextWrapper fw={600}>{procedureCode || EMPTY_CELL}</TextWrapper>
      </Table.Td>
      <Table.Td>
        <TextWrapper style={{ wordBreak: "break-word" }}>
          {codeDescription || EMPTY_CELL}
        </TextWrapper>
      </Table.Td>
      {/* The keys use the position because these values are not unique. A charge can carry the
          same modifier twice, and both have to show. */}
      <Table.Td>
        {modifiers.length
          ? modifiers.map((modifier, index) => (
              <TextWrapper key={`${id}-modifier-${index}`}>{modifier}</TextWrapper>
            ))
          : EMPTY_CELL}
      </Table.Td>
      <Table.Td>
        {diagnosisCodes.length
          ? diagnosisCodes.map((code, index) => (
              <TextWrapper key={`${id}-diagnosis-${index}`}>{code}</TextWrapper>
            ))
          : EMPTY_CELL}
      </Table.Td>
      <Table.Td ta="right">{units}</Table.Td>
      <Table.Td>
        <Group gap="md" wrap="nowrap" justify="flex-end">
          <BillerSectionRowAction
            icon={IconNote}
            label={canOpenComments ? "View Notes" : "Add Note"}
            actionTestId={`biller-section-row-note-button-${id}`}
            onClick={canOpenComments ? () => onOpenComments(row) : undefined}
          />
          <BillerSectionRowAction
            icon={IconTrash}
            label="Delete Procedure Code"
            color="var(--mantine-color-red-6)"
            onClick={onDelete && (() => onDelete(row))}
            actionTestId={`biller-section-row-delete-button-${id}`}
          />
          <BillerSectionRowAction
            icon={IconEdit}
            label="Edit Procedure Code"
            onClick={onEdit && (() => onEdit(row))}
            actionTestId={`biller-section-row-edit-button-${id}`}
          />
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * The encounter's captured charges, one row each.
 *
 * This component only displays the rows. Each row shows the design's three actions: note, delete and
 * edit. Delete works when the tab passes `onDelete`, edit when it passes `onEdit`, and note when it
 * passes `onOpenComments` — withheld on a staged row, since there is no captured `ChargeItem` yet for
 * its comments to live on. An action a row withholds shows the app's `Feature under development`
 * tooltip.
 */
function BillerSectionTable({ rows, onDelete, onEdit, onOpenComments }: BillerSectionTableProps) {
  return (
    <Table.ScrollContainer type="native" minWidth={BILLING_TABLE_MIN_WIDTH}>
      <Table verticalSpacing="md" data-testid="biller-section-table">
        <Table.Thead h="48px">
          <Table.Tr bg="gray.0">
            <Table.Th fw={600} c="gray.7" w={COLUMN_WIDTHS.procedureCode}>
              Procedure Code
            </Table.Th>
            <Table.Th fw={600} c="gray.7" w={COLUMN_WIDTHS.codeDescription}>
              Code Description
            </Table.Th>
            <Table.Th fw={600} c="gray.7" w={COLUMN_WIDTHS.modifiers}>
              Modifiers
            </Table.Th>
            <Table.Th fw={600} c="gray.7" w={COLUMN_WIDTHS.diagnosisCodes}>
              ICD-10
            </Table.Th>
            <Table.Th fw={600} c="gray.7" ta="right" w={COLUMN_WIDTHS.units}>
              Units
            </Table.Th>
            <Table.Th fw={600} c="gray.7" ta="right" w={COLUMN_WIDTHS.actions}>
              Actions
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <BillerSectionTableRow
              row={row}
              key={row.id}
              onEdit={onEdit}
              onDelete={onDelete}
              onOpenComments={onOpenComments}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export default BillerSectionTable;
